
import { Component, Prop, Vue } from "vue-property-decorator";
import { TSidebarLink } from "@/models/sidebar";
import { SMUser } from "@/store";

const ICON_CHEVRON_DOWN = "chevron-down";
const ICON_CHEVRON_UP = "chevron-up";

const ELEMENT_DIV = "div";
const ELEMENT_ROUTER_LINK = "router-link";

const CLASS = "sidebar-link";

@Component
export default class AppSidebarLink extends Vue implements TSidebarLink {
  @Prop({ type: String, required: true })
  readonly icon!: string;
  @Prop({ type: String, required: true })
  readonly label!: string;
  @Prop({ type: String }) readonly path?: string;
  @Prop({ type: String, default: "" })
  readonly childLabel!: string;
  @Prop({ type: Array, default: () => [] })
  readonly childLabels!: string[];
  @Prop({ type: Boolean, default: false })
  readonly isChild!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly avatar!: boolean;
  @Prop({ type: Function })
  readonly action?: () => void;
  @Prop({ type: Boolean, default: false })
  readonly hasChild!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly isOpen!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly selected?: boolean;
  @Prop({ type: Boolean, default: false })
  readonly collapsed?: boolean;

  get isHideChildOnCollapsed() {
    return this.isChild && this.collapsed;
  }

  get basePath() {
    return SMUser.basePath;
  }

  get routes() {
    return this.$router.options.routes;
  }

  deepRouteSearch(route, path) {
    let result = "";

    const currentPath = route.path.includes("/") ? route.path : `/${route.path}`;

    if (currentPath === path) result = route;

    if (route.children?.length) {
      route.children?.forEach((child) => {
        const childPath = `${currentPath}/${child.path}`;

        if (childPath === path) result = child;

        if (child.children?.length) {
          const search = this.deepRouteSearch(child, path);

          if (search) result = search;
        }
      });
    }

    if (result) return result;
  }

  get currentRoute() {
    return this.routes?.reduce((acc, route) => {
      const search = this.deepRouteSearch(route, this.path);

      if (search) acc = search;

      return acc;
    }, "");
  }

  get isActive() {
    const pathIncludesPathAlias = ((this.currentRoute as unknown) as any).meta?.aliasRoutes?.some(
      (alias) => {
        return this.$route.fullPath.includes(alias);
      }
    );

    return this.$route.fullPath.includes(this.path) || pathIncludesPathAlias;
  }

  get classes() {
    const { isChild, isActive, selected, collapsed } = this;
    return {
      [`${CLASS}_child`]: isChild,
      [`${CLASS}_active`]: isActive,
      [`${CLASS}_selected`]: selected,
      [`${CLASS}_collapsed`]: collapsed,
    };
  }

  get componentProps() {
    const { path } = this;

    if (!path) return { is: ELEMENT_DIV };

    return {
      is: ELEMENT_ROUTER_LINK,
      to: path,
      exact: path === "/",
    };
  }

  get componentListeners() {
    const { path, action } = this;

    if (!path && action) return { click: () => action() };

    return {};
  }

  get iconChevronName() {
    return this.isOpen ? ICON_CHEVRON_UP : ICON_CHEVRON_DOWN;
  }

  sidebarLinkToggle(label: string, hasChild: boolean, childLabel: string, childLabels: string[]) {
    if (!hasChild) return;

    this.$emit("sidebar-link-toggle", {
      label,
      childLabel,
      childLabels,
      isOpen: !this.isOpen,
    });
  }
}
